import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { getSlideDetailStore } from '@/common/stores/slide-detail';
import { USER_ENV_KEYS } from '@/common/define/userEnv.define';
import { usePersistedTab } from '@/common/composables/usePersistedTab';
import { storeToRefs } from 'pinia';
import { useInternational } from '@/common/locale';
import { NotificationStatusWindowInfo, NotificationType } from '@/alert/utils/notification.types';
import { cloneDeep, lowerCase } from 'lodash-es';
import { NOTIFICATION_STATUS_WINDOW_INITIAL_INFO } from '@/alert/utils/notification.define';
import { normalizeNotificationMethodType } from '@/alert/utils/notification.utils';
import { useAlertDetailStore } from '@/alert/stores/alert-detail';
import { getAlertType } from '@/alert/utils/utils';
import { CHECK_BY_TARGET } from '@/alert/utils/define';
import { useInsightInfo } from '@/alert/utils/alertInsight';
import type { AlertType, AlertSubType } from '@/alert/components/alertDetail/alertDetail.types';
import type { AlertRuleDetailV9, EventAlertMessageItem } from '@/api-client/alert/model';
import type { EventAlertDetailInfo } from '@/alert/utils/types';
import { getV9AlertEventDetail } from '@/api-client/alert/queries/event-alert-controller/event-alert-controller';
import { getV9AlertsAlertRulesByAlertRuleId } from '@/api-client/alert/queries/alert-rule-v9-controller/alert-rule-v9-controller';
import { useChatbotConfigStore } from '@/config/stores/chatbotConfig';

type TabValue = 'information' | 'history' | 'insightView' | 'aiAnalysis';

interface TabPanel {
  text: string;
  value: TabValue;
}

export interface Props {
  name?: string;
  type?: string;
  alertInfo?: EventAlertDetailInfo;
}

export const setup = (props: Props) => {
  const { t } = useInternational();
  const { setInsightView } = useInsightInfo();
  const isInsightView = ref(false);

  const slideDetailStore = getSlideDetailStore();
  const { setIsDetailLoading, selectedTabComputed } = slideDetailStore;
  const { selectedSlideTabPanel } = storeToRefs(slideDetailStore);

  const alertDetailStore = useAlertDetailStore();
  const { selectedRuleInfo } = storeToRefs(alertDetailStore);

  const isTabLoading = ref<boolean>(false);

  const targetInfo = ref<EventAlertDetailInfo>({
    ruleId: '',
    resultId: '',
    info: [],
    ruleType: CHECK_BY_TARGET,
    timePeriodInfo: {
      timePeriod: 'p10m',
      fromTime: '',
      toTime: '',
      fromTimeUtc: '',
      toTimeUtc: '',
      isPaused: false,
    },
  });
  const eventData = ref<EventAlertMessageItem | undefined>(undefined);
  const ruleData = ref<AlertRuleDetailV9 | undefined>(undefined);
  const isFetching = ref(false);

  const defaultTabPanels: TabPanel[] = [
    { text: t('WORD.INFORMATION'), value: 'information' },
    { text: t('WORD.HISTORY'), value: 'history' },
  ];

  // 알람 상세 'AI 분석' 탭 — 환경설정 alarmDetailRcaMode 가 off 가 아니고 챗봇 enabled 일 때 노출.
  const { settings: chatbotSettings } = storeToRefs(useChatbotConfigStore());
  const aiAnalysisEnabled = computed(
    () =>
      chatbotSettings.value?.enabled !== false &&
      (chatbotSettings.value?.alarmDetailRcaMode ?? 'manual') !== 'off',
  );

  const tabPanels = computed(() => {
    const panels: TabPanel[] = [...defaultTabPanels];
    if (isInsightView.value) {
      panels.push({ text: t('WORD.INSIGHT_VIEW'), value: 'insightView' });
    }
    if (aiAnalysisEnabled.value) {
      panels.push({ text: t('WORD.AI_ANALYSIS'), value: 'aiAnalysis' });
    }
    return panels;
  });

  /**
   * AI 분석에 넘길 풍부한 알람 맥락 — 특정 발생시점(triggerTime) + 대상(tagName/tagId) + 심각도.
   * 이벤트 알람 상세는 발생 occurrence 단위라 '특정 시점·대상' RCA(detailed)를 강화한다.
   */
  const aiAnalysisContext = computed(() => {
    const info = targetInfo.value?.info?.[0];
    const tag = info?.tags?.[0];
    return {
      alertRuleId: targetInfo.value?.ruleId || eventData.value?.alertRuleId,
      targetId: tag?.tagId,
      resultId: targetInfo.value?.resultId, // 동기 occurrence ID — RCA 안정 캐시 키
      ruleName: info?.ruleName || eventData.value?.alertRuleName,
      ruleType: info?.type,
      level: info?.level,
      triggerTime: eventData.value?.triggerTime,
      targetName: tag?.tagName,
      targetKind: tag?.tagType || info?.type,
    };
  });

  const { getSavedTab, saveTab } = usePersistedTab(
    USER_ENV_KEYS.TAB_alert_eventAlertDetail_selected,
  );
  const selectedTab = selectedTabComputed(() => getSavedTab({ panels: tabPanels.value }));
  watch(selectedTab, saveTab);

  let loadingCnt = 0;

  const onChangeLoadingState = (v: boolean) => {
    loadingCnt += v ? 1 : -1;
    if (loadingCnt < 0) {
      loadingCnt = 0;
    }
    setIsDetailLoading(loadingCnt > 0);
  };

  const notificationStatusWindowInfo = ref<NotificationStatusWindowInfo>(
    cloneDeep(NOTIFICATION_STATUS_WINDOW_INITIAL_INFO),
  );

  const onClickStatusIcon = (iconType: NotificationType, resultId: string) => {
    const notificationStatus = eventData.value?.notificationStatus ?? [];
    const availableStatus = notificationStatus.map((status) =>
      normalizeNotificationMethodType(status.methodType),
    );

    notificationStatusWindowInfo.value = {
      isShow: true,
      availableStatus,
      resultId,
      selectedType: iconType,
    };
  };

  // 알람 A→B 빠른 전환 시 늦게 도착한 A 응답이 B 위에 덮이면, AI 분석 컨텍스트가
  // B 의 rule/target + A 의 triggerTime 으로 섞여 잘못된 RCA 가 생성·캐시될 수 있다
  // (MR !13408 리뷰 P1-5). 최신 요청 seq 가 아니면 응답을 폐기한다.
  let eventInfoReqSeq = 0;

  const getEventAlertInformation = async () => {
    const { ruleId: alertRuleId, resultId, info } = targetInfo.value;
    if (!alertRuleId || !resultId) return;

    const seq = ++eventInfoReqSeq;
    // 전환 즉시 이전 알람 데이터를 비운다 — 응답 도착 전까지 B 의 rule/target + A 의
    // triggerTime 혼합 컨텍스트가 만들어져 자동 RCA 가 잘못된 키로 실행·캐시되는 것 방지 (재리뷰 P1)
    eventData.value = undefined;
    ruleData.value = undefined;
    const isSystemType = info?.[0]?.type?.toLowerCase() === 'system';

    try {
      onChangeLoadingState(true);

      if (isSystemType) {
        // System 타입: EventAlertDetail API만 호출
        const eventResponse = await getV9AlertEventDetail({
          alertRuleId,
          resultId,
        });
        if (seq !== eventInfoReqSeq) return;

        eventData.value = eventResponse?.data?.data?.[0];
        ruleData.value = undefined;
        alertDetailStore.setUserAlertInformationData(null);
      } else {
        const [eventResponse, ruleResponse] = await Promise.all([
          getV9AlertEventDetail({ alertRuleId, resultId }),
          getV9AlertsAlertRulesByAlertRuleId(alertRuleId),
        ]);
        if (seq !== eventInfoReqSeq) return;

        eventData.value = eventResponse.data?.data?.[0];
        ruleData.value = ruleResponse.data?.data?.[0];
      }
    } catch (e) {
      console.log(e);
      // 실패 시 이전 알람의 eventData(triggerTime 등) 잔존 방지 — stale 교차오염 차단.
      // 단, 늦게 실패한 이전 요청이 최신 요청의 정상 데이터를 지우면 안 되므로 seq 가드.
      if (seq === eventInfoReqSeq) {
        eventData.value = undefined;
        ruleData.value = undefined;
      }
    } finally {
      onChangeLoadingState(false);
    }
  };

  const setInsightViewData = () => {
    const subType = selectedRuleInfo.value?.subType?.toLowerCase();
    const lastLevel =
      subType === 'metric'
        ? eventData.value?.messageInfo?.metricConditions?.[0]?.alertLevel
        : eventData.value?.messageInfo?.eventConditions?.[0]?.level;

    if (ruleData.value && lastLevel !== undefined) ruleData.value.lastLevel = lastLevel;
    alertDetailStore.setUserAlertInformationData(ruleData.value ?? null);
  };

  watch(
    selectedSlideTabPanel,
    async (v) => {
      if (v?.type !== 'Event Alert') return;
      targetInfo.value = v?.addition ?? targetInfo.value;
    },
    {
      immediate: true,
    },
  );

  watch(
    () => props.alertInfo,
    async () => {
      if (props?.type !== 'Event Alert' || !props?.alertInfo?.resultId) return;
      targetInfo.value = props?.alertInfo;
    },
    {
      immediate: true,
      deep: true,
    },
  );

  // AlertHistory 컴포넌트에서 사용하는 useAlertDetailStore에 필요한 정보 설정
  // targetInfo가 변경되었을 때만 실행
  watch(
    targetInfo,
    async (newTargetInfo, oldTargetInfo) => {
      if (!newTargetInfo?.resultId || newTargetInfo.resultId === oldTargetInfo?.resultId) return;

      selectedRuleInfo.value = {
        alertRuleName: newTargetInfo.info[0].ruleName ?? '',
        alertRuleType: getAlertType(newTargetInfo.info[0].type?.toLowerCase() ?? '') as AlertType,
        subType: lowerCase(newTargetInfo.info[0].type ?? '') as AlertSubType,
        alertRuleId: newTargetInfo.ruleId,
        ruleCriteria: newTargetInfo.ruleType,
        ...(newTargetInfo.ruleType === CHECK_BY_TARGET &&
          newTargetInfo.info[0].tags?.[0]?.tagId && {
            alertTargetId: newTargetInfo.info[0].tags[0].tagId,
          }),
      };

      isFetching.value = true;
      try {
        await getEventAlertInformation();
        setInsightViewData();
        setInsightView(isInsightView, ruleData.value);

        if (!isInsightView.value && selectedTab.value === 'insightView') {
          selectedTab.value = 'information';
        }
      } finally {
        isFetching.value = false;
      }
    },
    {
      immediate: true,
      deep: true,
    },
  );

  onBeforeUnmount(() => {
    alertDetailStore.reset();
  });

  return {
    selectedTab,
    tabPanels,
    targetInfo,
    eventData,
    ruleData,
    isFetching,
    notificationStatusWindowInfo,
    isTabLoading,
    onChangeLoadingState,
    onClickStatusIcon,
    aiAnalysisContext,
  };
};
